import { useState } from 'react';
import type { ThemeKey } from '@/pages/home/themes';
import {
  CONTENT_INK,
  CONTENT_MUTED,
  CONTENT_BORDER,
  CONTENT_GREEN,
  accentOf,
} from '@/pages/home/contentTokens';

interface Course {
  id: string;
  name: string;
  org: string;
  track: string;
  free: boolean;
  self: string;
  emp: number;
  retain: number;
  rating: number;
}

const COURSES: Course[] = [
  { id: 'ai', name: '생성형 AI 풀스택', org: 'K-디지털 아카데미', track: 'AI·데이터', free: true, self: '0원', emp: 88.4, retain: 91.2, rating: 4.8 },
  { id: 'cloud', name: '클라우드 백엔드', org: '글로벌 SW 캠퍼스', track: '클라우드', free: false, self: '350,000원', emp: 79.2, retain: 82.0, rating: 4.5 },
  { id: 'data', name: '데이터 분석 부트캠프', org: '한국데이터스쿨', track: 'AI·데이터', free: true, self: '0원', emp: 84.1, retain: 87.5, rating: 4.7 },
  { id: 'web', name: '웹 프론트엔드 과정', org: '코드베이스랩', track: '웹·백엔드', free: false, self: '120,000원', emp: 76.8, retain: 80.1, rating: 4.3 },
  { id: 'ux', name: 'UI/UX 디자인 스쿨', org: '픽셀아카데미', track: '디자인', free: true, self: '0원', emp: 81.5, retain: 84.0, rating: 4.6 },
];

const PICK_LABELS = ['A', 'B', 'C', 'D'];
const MAX_PICK = 4;
const MIN_PICK = 2;

export default function CompareSection({ theme }: { theme: ThemeKey }) {
  const accent = accentOf(theme);
  const [selected, setSelected] = useState<string[]>(['ai', 'cloud', 'data']);

  const toggle = (id: string) => {
    setSelected((prev) => {
      if (prev.includes(id)) {
        if (prev.length <= MIN_PICK) return prev;
        return prev.filter((x) => x !== id);
      }
      if (prev.length >= MAX_PICK) return prev;
      return [...prev, id];
    });
  };

  const picked = COURSES.filter((c) => selected.includes(c.id));

  return (
    <section id="compare" className="max-w-[1180px] mx-auto px-8 md:px-14 pt-16 pb-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
        <div className="lg:col-span-7">
          <div className="text-[11px] font-semibold tracking-wide mb-6" style={{ color: accent }}>
            한눈에 보는 과정비교
          </div>
          <h2
            className="font-logo-b text-3xl md:text-4xl leading-tight"
            style={{ color: CONTENT_INK, letterSpacing: '-0.04em' }}
          >
            과정끼리,<br />나란히 놓고.
          </h2>
        </div>
        <div className="lg:col-span-5 flex items-end">
          <p style={{ fontSize: '15px', lineHeight: 1.7, color: CONTENT_INK, opacity: 0.7 }}>
            실자부담·실질 취업률·고용유지율·평점을 2~4개 과정까지 한 화면에 올려
            공공데이터 기준으로 대조합니다. 과정을 눌러 추가하거나 빼보세요.
          </p>
        </div>
      </div>

      {/* 과정 선택 칩 */}
      <div className="flex flex-wrap gap-2 mb-6">
        {COURSES.map((c) => {
          const active = selected.includes(c.id);
          const pickIdx = selected.indexOf(c.id);
          return (
            <button
              key={c.id}
              type="button"
              aria-pressed={active}
              onClick={() => toggle(c.id)}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full px-4 h-[36px] text-[12px] font-semibold cursor-pointer transition-all"
              style={{
                background: active ? accent : '#fff',
                color: active ? '#fff' : CONTENT_INK,
                border: `1px solid ${active ? accent : CONTENT_BORDER}`,
              }}
            >
              {active && (
                <span className="w-[18px] h-[18px] rounded-full flex items-center justify-center text-[10px] font-bold" style={{ background: 'rgba(255,255,255,0.25)' }}>
                  {PICK_LABELS[pickIdx]}
                </span>
              )}
              {c.name}
            </button>
          );
        })}
      </div>

      <div className="mb-5 flex items-center gap-3 text-[11px]" style={{ color: CONTENT_MUTED }}>
        <span className="inline-flex items-center gap-1.5">
          <i className="ri-scales-3-line" style={{ color: accent }} />
          {selected.length}개 과정 비교 중 · 최소 {MIN_PICK}개 ~ 최대 {MAX_PICK}개
        </span>
      </div>

      {/* 비교 카드 그리드 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {picked.map((c, idx) => (
          <div
            key={c.id}
            className="rounded-[28px] p-5 flex flex-col"
            style={{
              border: `1px solid ${idx === 0 ? accent : CONTENT_BORDER}`,
              background: idx === 0 ? `${accent}0d` : '#fff',
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <span className="inline-flex items-center justify-center w-[26px] h-[26px] rounded-lg text-[11px] font-bold" style={{ background: accent, color: '#fff' }}>
                {PICK_LABELS[idx]}
              </span>
              <span
                className="text-[10px] font-bold px-2.5 h-[22px] inline-flex items-center rounded-full"
                style={{
                  color: c.free ? CONTENT_GREEN : CONTENT_MUTED,
                  border: `1px solid ${c.free ? 'rgba(62,142,90,0.32)' : CONTENT_BORDER}`,
                  background: c.free ? 'rgba(62,142,90,0.08)' : 'transparent',
                }}
              >
                {c.free ? '자부담 0원' : '자부담 발생'}
              </span>
            </div>

            <div className="text-[10px] tracking-[0.2em] uppercase mb-1" style={{ color: CONTENT_MUTED }}>{c.track}</div>
            <div className="font-logo-b text-[20px] leading-tight mb-1" style={{ color: CONTENT_INK, letterSpacing: '-0.02em' }}>
              {c.name}
            </div>
            <div className="text-[11.5px] mb-5" style={{ color: CONTENT_MUTED }}>{c.org}</div>

            <div className="space-y-3.5 mt-auto">
              <div className="flex items-baseline justify-between">
                <span className="text-[11px]" style={{ color: CONTENT_MUTED }}>실자부담</span>
                <span className="text-[13px] font-semibold" style={{ color: c.free ? CONTENT_GREEN : CONTENT_INK }}>{c.self}</span>
              </div>

              <div>
                <div className="flex items-baseline justify-between mb-1.5">
                  <span className="text-[11px]" style={{ color: CONTENT_MUTED }}>실질 취업률</span>
                  <span className="text-[13px] font-semibold" style={{ color: CONTENT_INK }}>{c.emp}%</span>
                </div>
                <div className="h-[6px] w-full rounded-full overflow-hidden" style={{ background: 'rgba(20,18,16,0.08)' }}>
                  <div className="h-full rounded-full" style={{ width: `${c.emp}%`, background: accent }} />
                </div>
              </div>

              <div className="flex items-baseline justify-between">
                <span className="text-[11px]" style={{ color: CONTENT_MUTED }}>6개월 고용유지</span>
                <span className="text-[13px] font-semibold" style={{ color: CONTENT_INK }}>{c.retain}%</span>
              </div>

              <div className="flex items-baseline justify-between">
                <span className="text-[11px]" style={{ color: CONTENT_MUTED }}>HRD 평점</span>
                <span className="text-[13px] font-semibold flex items-center gap-1" style={{ color: '#C6892B' }}>
                  <i className="ri-star-fill text-[11px]" />
                  {c.rating.toFixed(1)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 flex items-center gap-2 text-[10.5px]" style={{ color: CONTENT_MUTED }}>
        <i className="ri-shield-keyhole-line" style={{ color: accent }} />
        DATA SYNC: HRD-NET · 고용보험 DB 기준 · 2026 실측
      </div>
    </section>
  );
}